const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api').replace(/\/+$/, '');
const OTP_API_PATH = String(process.env.REACT_APP_OTP_API_PATH || 'otp_delete').replace(/^\/+/, '');
export const OTP_API_URL = `${API_BASE_URL}/${OTP_API_PATH}`;
const TOKEN_KEY = 'authToken';
const ADMIN_KEY = 'admin';
const BRANCH_KEY = 'selected_branch_id';
export const AUTH_STATE_EVENT = 'auth-state-changed';

let fetchPatched = false;

function readJsonFromStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function parseBranchId(value) {
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
}

function emitAuthStateChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(AUTH_STATE_EVENT));
}

function syncSelectedBranchFromAdmin(admin) {
  const nextAdmin = admin && typeof admin === 'object' ? admin : null;
  if (!nextAdmin) {
    localStorage.removeItem(BRANCH_KEY);
    return;
  }

  if (String(nextAdmin.role || '') === 'branch_admin') {
    const branchId = parseBranchId(nextAdmin.branch_id);
    if (branchId) {
      localStorage.setItem(BRANCH_KEY, String(branchId));
    }
    return;
  }

  const allowedIds = (Array.isArray(nextAdmin.allowed_branches) ? nextAdmin.allowed_branches : [])
    .map((b) => parseBranchId(b?.id))
    .filter(Boolean);
  const selected = parseBranchId(localStorage.getItem(BRANCH_KEY));
  if (selected && allowedIds.includes(selected)) {
    return;
  }

  const active = parseBranchId(nextAdmin.active_branch_id);
  const nextBranchId = active && allowedIds.includes(active) ? active : allowedIds[0] || null;
  if (nextBranchId) {
    localStorage.setItem(BRANCH_KEY, String(nextBranchId));
  }
}

export const apiUrl = (path = '') => {
  const cleanPath = String(path || '').replace(/^\/+/, '');
  return cleanPath ? `${API_BASE_URL}/${cleanPath}` : API_BASE_URL;
};

export const getStoredAdmin = () => readJsonFromStorage(ADMIN_KEY);

export const getStoredToken = () => String(localStorage.getItem(TOKEN_KEY) || '').trim();

export const setStoredToken = (token) => {
  const value = String(token || '').trim();
  if (!value) {
    localStorage.removeItem(TOKEN_KEY);
  } else {
    localStorage.setItem(TOKEN_KEY, value);
  }
};

export const setStoredAdmin = (admin) => {
  if (!admin || typeof admin !== 'object') {
    localStorage.removeItem(ADMIN_KEY);
    syncSelectedBranchFromAdmin(null);
    emitAuthStateChanged();
    return;
  }

  localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
  syncSelectedBranchFromAdmin(admin);
  emitAuthStateChanged();
};

export const setStoredAuth = ({ token, admin }) => {
  setStoredToken(token);
  setStoredAdmin(admin);
};

export const getSelectedBranchId = () => {
  const admin = getStoredAdmin();
  if (!admin) return null;

  if (String(admin.role || '') === 'branch_admin') {
    return parseBranchId(admin.branch_id);
  }

  const selected = parseBranchId(localStorage.getItem(BRANCH_KEY));
  if (selected) return selected;

  const active = parseBranchId(admin.active_branch_id);
  if (active) return active;

  const list = Array.isArray(admin.allowed_branches) ? admin.allowed_branches : [];
  for (const b of list) {
    const id = parseBranchId(b?.id);
    if (id) return id;
  }
  return null;
};

export const setSelectedBranchId = (branchId) => {
  const parsed = parseBranchId(branchId);
  if (!parsed) return;
  localStorage.setItem(BRANCH_KEY, String(parsed));
  emitAuthStateChanged();
};

export const clearAuthState = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ADMIN_KEY);
  localStorage.removeItem(BRANCH_KEY);
  emitAuthStateChanged();
};

export const installApiFetchInterceptor = () => {
  if (fetchPatched || typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const requestUrl = typeof input === 'string' ? input : String(input?.url || '');
    const isApi = requestUrl.startsWith(API_BASE_URL);
    if (!isApi) {
      return nativeFetch(input, init);
    }

    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    const token = getStoredToken();
    const branchId = getSelectedBranchId();

    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (branchId && !headers.has('x-branch-id')) {
      headers.set('x-branch-id', String(branchId));
    }

    const execute = input instanceof Request
      ? nativeFetch(new Request(input, { ...init, headers }))
      : nativeFetch(input, { ...init, headers });

    return execute.then((response) => {
      const nextToken = String(response.headers.get('x-auth-token') || '').trim();
      if (nextToken) {
        setStoredToken(nextToken);
      }

      if (response.status === 401 && getStoredToken()) {
        clearAuthState();
      }

      return response;
    });
  };

  fetchPatched = true;
};

export const refreshAuthSession = async () => {
  const token = getStoredToken();
  const admin = getStoredAdmin();
  if (!token || !admin) return null;

  const res = await window.fetch(apiUrl('auth_session'));
  const data = await res.json().catch(() => null);

  if (!res.ok || data?.status !== 'ok' || !data?.token || !data?.admin) {
    if (res.status === 401) {
      clearAuthState();
    }
    return null;
  }

  setStoredAuth({
    token: data.token,
    admin: data.admin,
  });

  return data;
};

export default API_BASE_URL;
